import React, { useEffect, useMemo, useState } from "react";
import {
  buildFlowVariableCatalog,
  buildFlowVariableSections,
  CUSTOM_SECTION_ID,
  getFlowVariableMeta,
} from "./flowVariableCatalog";

/**
 * Dropdown to insert {{variable}} tokens into message / question nodes.
 *
 * @param {object} props
 * @param {string[]} [props.customVariables] Answer variable names from this chatbot flow
 * @param {(variableId: string) => void} props.onInsert
 * @param {string} [props.label]
 * @param {boolean} [props.showInsertButton]
 * @param {string} [props.insertButtonLabel]
 * @param {string[]} [props.sectionIds] Limit visible sections (e.g. ticket actions → default + ticket_booking)
 */
export default function FlowVariableInsertPicker({
  customVariables = [],
  onInsert,
  label = "Insert variable",
  showInsertButton = true,
  insertButtonLabel = "Insert into message",
  sectionIds = null,
}) {
  const sections = useMemo(() => {
    const all = buildFlowVariableSections(customVariables);
    if (!Array.isArray(sectionIds) || !sectionIds.length) {
      return all;
    }
    const allowed = new Set(sectionIds);
    return all.filter((section) => allowed.has(section.id));
  }, [customVariables, sectionIds]);

  const catalog = useMemo(
    () => buildFlowVariableCatalog(customVariables),
    [customVariables],
  );

  const firstSelectableId = useMemo(() => {
    for (const section of sections) {
      if (section.variables.length > 0) {
        return section.variables[0].id;
      }
    }
    return "";
  }, [sections]);

  const [selectedId, setSelectedId] = useState(() => firstSelectableId);

  useEffect(() => {
    if (!firstSelectableId) return;
    if (!catalog.some((row) => row.id === selectedId)) {
      setSelectedId(firstSelectableId);
    }
  }, [catalog, selectedId, firstSelectableId]);

  const selected = useMemo(
    () => getFlowVariableMeta(selectedId, catalog) || catalog[0] || null,
    [catalog, selectedId],
  );

  const handleInsert = () => {
    if (!selected?.id || !onInsert) return;
    onInsert(selected.id);
  };

  if (!catalog.length) {
    return null;
  }

  return (
    <div
      style={{
        marginTop: 8,
        padding: 10,
        borderRadius: 8,
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "flex-end",
        }}
      >
        <label style={{ flex: "1 1 220px", fontSize: 12, color: "#334155" }}>
          <span style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            {label}
          </span>
          <select
            value={selected?.id || ""}
            onChange={(event) => setSelectedId(event.target.value)}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              fontSize: 13,
              background: "#fff",
            }}
          >
            {sections.map((section) => (
              <optgroup key={section.id} label={section.label}>
                {section.variables.length > 0 ? (
                  section.variables.map((variable) => (
                    <option key={variable.id} value={variable.id}>
                      {`{{${variable.id}}}`}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    {section.emptyHint || "No variables in this section"}
                  </option>
                )}
              </optgroup>
            ))}
          </select>
        </label>
        {showInsertButton ? (
          <button
            type="button"
            onClick={handleInsert}
            disabled={!selected?.id}
            style={{
              padding: "8px 14px",
              borderRadius: 6,
              border: "none",
              background: selected?.id ? "#0f766e" : "#94a3b8",
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: selected?.id ? "pointer" : "not-allowed",
              whiteSpace: "nowrap",
            }}
          >
            {insertButtonLabel}
          </button>
        ) : null}
      </div>
      {selected?.description ? (
        <p
          style={{
            margin: "8px 0 0",
            fontSize: 12,
            lineHeight: 1.45,
            color: "#475569",
          }}
        >
          {selected.sectionId === CUSTOM_SECTION_ID ? (
            <>
              <strong>Custom: </strong>
              {selected.description}
            </>
          ) : (
            selected.description
          )}
        </p>
      ) : null}
      {!customVariables?.length ? (
        <p style={{ margin: "6px 0 0", fontSize: 11, color: "#64748b" }}>
          Custom variables appear when you add Question nodes with an Answer variable name upstream in this flow.
        </p>
      ) : null}
    </div>
  );
}
